import { useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

// Utils
import { cn } from 'utils';

// Styles
import styles from './MapBlock.module.css';

// Constants
import { MAP_LIBRARIES, MAP_TRAVEL_MODES, MAP_TRAVEL_MODES_ICON } from '../../../src/constants';

// Components
import RichTextContent from '../RichTextContent/RichTextContent';
import OptionButton from '../OptionButton/OptionButton';
import TextField from '../TextField/TextField';
import { Picto } from '../Picto/Picto';

const GoogleMapDirections = dynamic(() => import('../GoogleMapDirections/GoogleMapDirections'), {
  ssr: false,
});

function MapBlock({
  content, origin, address, theme, className,
}) {
  const { t } = useTranslation();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const [selectedMode, setSelectedMode] = useState(MAP_TRAVEL_MODES.WALKING);
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [destination, setDestination] = useState({});

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
  };

  const onPlaceChanged = () => {
    if (autocompleteValue && autocompleteValue !== null) {
      const place = autocompleteValue.getPlace();
      const lat = place?.geometry?.location?.lat();
      const lng = place?.geometry?.location?.lng();
      const formattedAddress = place?.formatted_address;
      if (formattedAddress) { setDestination({ formattedAddress, location: { lat, lng } }); }
    }
  };
  return (
    <div id="map" className={cn([styles.wrapper, styles[theme], className])}>
      <div className={styles.container}>
        <div className={styles.map}>
          {isLoaded ? (
            <GoogleMapDirections
              selectedMode={selectedMode}
              destination={destination}
              origin={origin}
            />
          ) : null}
        </div>
        <div className={styles.destinationsContainer}>
          <div className={cn([styles.formContainer, styles[theme]])}>
            <div className={styles.form}>
              <div className={cn([styles.travelModes, styles.noScroll])}>
                <OptionButton
                  icon={MAP_TRAVEL_MODES_ICON.WALKING}
                  onClick={() => handleModeChange(MAP_TRAVEL_MODES.WALKING)}
                  active={selectedMode === MAP_TRAVEL_MODES.WALKING}
                  color="white"
                >
                  {t(`residence.travelMode.${MAP_TRAVEL_MODES.WALKING}`)}
                </OptionButton>
                <OptionButton
                  icon={MAP_TRAVEL_MODES_ICON.TRANSIT}
                  onClick={() => handleModeChange(MAP_TRAVEL_MODES.TRANSIT)}
                  active={selectedMode === MAP_TRAVEL_MODES.TRANSIT}
                  color="white"
                >
                  {t(`residence.travelMode.${MAP_TRAVEL_MODES.TRANSIT}`)}
                </OptionButton>
                <OptionButton
                  icon={MAP_TRAVEL_MODES_ICON.DRIVING}
                  onClick={() => handleModeChange(MAP_TRAVEL_MODES.DRIVING)}
                  active={selectedMode === MAP_TRAVEL_MODES.DRIVING}
                  color="white"
                >
                  {t(`residence.travelMode.${MAP_TRAVEL_MODES.DRIVING}`)}
                </OptionButton>
                <OptionButton
                  icon={MAP_TRAVEL_MODES_ICON.BICYCLING}
                  onClick={() => handleModeChange(MAP_TRAVEL_MODES.BICYCLING)}
                  active={selectedMode === MAP_TRAVEL_MODES.BICYCLING}
                  color="white"
                >
                  {t(`residence.travelMode.${MAP_TRAVEL_MODES.BICYCLING}`)}
                </OptionButton>
              </div>
              <div className={styles.inputsContainer}>
                <div className={styles.locationIcons}>
                  <Picto icon="locationOn" className={styles.icon} />
                  <span className={styles.locationSeparator} />
                  <Picto icon="locationSearching" className={styles.icon} />
                </div>
                <div className={styles.inputs}>
                  <input type="text" value={address} disabled className={cn([styles.fakeInput, styles.noScroll])} />
                  {isLoaded ? (
                    <Autocomplete
                      onPlaceChanged={onPlaceChanged}
                      onLoad={(autocomplete) => {
                        setAutocompleteValue(autocomplete);
                      }}
                      className={styles.input}
                    >
                      <TextField
                        name="autocomplete"
                        type="text"
                        color={theme}
                        placeholder={t('residence.searchLocationPlaceholder')}
                        aria-label={t('residence.searchLocationPlaceholder')}
                        className={styles.input}
                      />
                    </Autocomplete>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className={cn([styles.infoContainer, styles[theme]])}>
            <div className={styles.info}>
              <RichTextContent content={content} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

MapBlock.propTypes = {
  content: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark', 'neutral', '']),
  origin: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  address: PropTypes.string,
  className: PropTypes.string,
};

MapBlock.defaultProps = {
  content: '',
  theme: 'light',
  origin: null,
  address: '',
  className: '',
};

export default MapBlock;
